import React, { useEffect, useState } from 'react';
import { Alert, Box, CircularProgress, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { AxiosInstanceLongTimeOut } from '@service/axios-instance';
import { buildMachineUserWeek, machineUserWeek, UserDay } from '../utils/machine-user-week';
import { MachineUserHeartbeat } from '../utils/machine-user-analytics';

export default function MachineUserWeekTable({ operation, selectedUser }: { operation?: string | null; selectedUser: string }) {
  const [result, setResult] = useState<{ key: string; days: UserDay[]; error?: string } | null>(null);
  const key = `${operation}/${selectedUser}`;
  const current = result?.key === key ? result : null;

  useEffect(() => {
    if (!operation || !selectedUser) return;
    const controller = new AbortController();
    const now = new Date();
    const days = machineUserWeek(now);
    const config = { withCredentials: true, signal: controller.signal };
    const users: Array<string | number> = [selectedUser];
    if (selectedUser.trim() && Number.isFinite(Number(selectedUser))) {
      users.push(Number(selectedUser));
      if (String(Number(selectedUser)) !== selectedUser) users.push(String(Number(selectedUser)));
    }
    const request = { start_date: days[0].start.toISOString(), end_date: now.toISOString(), match_filters_dict: { user: users } };
    // Include the preceding heartbeat so an operating interval can cross midnight.
    const heartbeatRequest = { ...request, start_date: new Date(days[0].start.getTime() - 5 * 60000).toISOString() };
    const db = encodeURIComponent(operation);
    setResult(null);
    Promise.all([
      AxiosInstanceLongTimeOut.post(`/heartbeats/get_heartbeats?db=${db}`, heartbeatRequest, config).then(async response => {
        const ids = Array.from(new Set((response.data as MachineUserHeartbeat[] || []).map(h => h.id).filter(id => id != null)));
        if (!ids.length) return [];
        // Other operators' readings stop this user's time at a handover.
        const all = await AxiosInstanceLongTimeOut.post(`/heartbeats/get_heartbeats?db=${db}`,
          { ...heartbeatRequest, match_filters_dict: { id: ids } }, config);
        return all.data || [];
      }),
      AxiosInstanceLongTimeOut.post(`/events/get_events?db=${db}`, request, config),
      AxiosInstanceLongTimeOut.post(`/checklists/get_checklists?db=${db}`, request, config),
    ]).then(([heartbeats, events, checklists]) => {
      if (!controller.signal.aborted) setResult({ key, days: buildMachineUserWeek(selectedUser, heartbeats, events.data || [], checklists.data || [], now) });
    }).catch(() => {
      if (!controller.signal.aborted) setResult({ key, days: [], error: 'Não foi possível carregar o resumo dos últimos 14 dias.' });
    });
    return () => controller.abort();
  }, [operation, selectedUser, key]);

  const metrics: Array<{ label: string; field: 'hours' | 'crashes' | 'holes' | 'checklists' }> = [
    { label: 'Horas trabalhadas', field: 'hours' }, { label: 'Batidas', field: 'crashes' },
    { label: 'Buracos', field: 'holes' }, { label: 'Checklists', field: 'checklists' },
  ];
  return <Paper elevation={5} sx={{ p: 2.5 }}>
    <Typography variant="h6" fontWeight={700}>Resumo dos últimos 14 dias</Typography>
    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
      Hoje e os 13 dias anteriores · horário de Brasília · hoje parcial
    </Typography>
    {!operation || !selectedUser ? <Typography>Selecione um usuário de máquina.</Typography>
      : !current ? <Box role="status" sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}><CircularProgress size={22} />Carregando resumo...</Box>
      : current.error ? <Alert severity="error">{current.error}</Alert>
      : <TableContainer>
        <Table size="small" aria-label="Métricas diárias do usuário nos últimos 14 dias" sx={{ minWidth: 1200 }}>
          <TableHead><TableRow sx={{ bgcolor: 'action.hover' }}>
            <TableCell sx={{ fontWeight: 700 }}>Métrica</TableCell>
            {current.days.map((day, index) => <TableCell key={day.key} align="right" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
              {day.start.toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo', day: '2-digit', month: '2-digit' })}
              {index === current.days.length - 1 && <Typography variant="caption" display="block">Hoje</Typography>}
            </TableCell>)}
          </TableRow></TableHead>
          <TableBody>{metrics.map(metric => <TableRow key={metric.field} hover>
            <TableCell component="th" scope="row" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{metric.label}</TableCell>
            {current.days.map(day => <TableCell key={day.key} align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
              {metric.field === 'hours' ? `${day.hours.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} h` : day[metric.field].toLocaleString('pt-BR')}
            </TableCell>)}
          </TableRow>)}</TableBody>
        </Table>
        <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1.5 }}>
          Horas em estado Operando, limitando intervalos sem comunicação a 5 minutos. Checklists incluem todos os status. Eventos sem usuário identificado não entram nesta contagem.
        </Typography>
      </TableContainer>}
  </Paper>;
}
